import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { logger } from '../../lib/logger';

// Базовая конфигурация API
// В production используем относительный путь /api - Vercel проксирует на api.constanta.dev
// В development используем localhost:3001/api
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api');

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// Создаем экземпляр axios
// ВАЖНО: НЕ устанавливаем Content-Type по умолчанию!
// Axios автоматически установит правильный Content-Type:
// - 'application/json' для JSON объектов
// - 'multipart/form-data' с boundary для FormData
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds для загрузки файлов
  withCredentials: true, // Send cookies for HTTP-only auth
});

// Request Interceptor - добавляем JWT токен к каждому запросу
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - обрабатываем ошибки и автоматический refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error: string; code?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if token expired and we haven't already tried to refresh
    if (
      error.response?.status === 401 &&
      error.response?.data?.code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // If refresh is in progress, wait for it and retry
        return new Promise(resolve => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Call refresh endpoint
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken
        }, {
          withCredentials: true
        });

        const { token: newAccessToken, refreshToken: newRefreshToken } = response.data;

        // Store new tokens
        localStorage.setItem('auth_token', newAccessToken);
        localStorage.setItem('refresh_token', newRefreshToken);

        // Update authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        isRefreshing = false;
        onRefreshed(newAccessToken);

        // Retry original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;

        // Clear tokens and redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');

        // Trigger auth state update - will redirect to login
        window.dispatchEvent(new CustomEvent('auth:logout'));

        return Promise.reject(refreshError);
      }
    }

    // Dead Google connection (revoked/expired refresh token). The backend now
    // returns 401 { code: 'GOOGLE_REAUTH_REQUIRED', needsAuth: true } from every
    // Google endpoint. Broadcast a single global event so the app can show one
    // "Reconnect Google" affordance instead of each page guessing.
    const data = error.response?.data as { code?: string; needsAuth?: boolean } | undefined;
    if (data?.code === 'GOOGLE_REAUTH_REQUIRED' || data?.needsAuth === true) {
      window.dispatchEvent(new CustomEvent('google:reauth'));
    }

    // Log error for debugging (logger handles dev mode check)
    logger.error('API Error:', {
      url: error.config?.url,
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });

    return Promise.reject(error);
  }
);

export default apiClient;
